import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Empty, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import style from './index.scss';
import {
  EVENT_CATEGORY,
  IDENTIFIER_KEY,
  MAX_ON_CLICK_FIELDS,
  MAX_SPATIAL_FIELDS,
  isSystemType
} from './constants';
import CategoryTabs from './CategoryTabs';
import EventTypeList from './EventTypeList';
import EventTypeHeader from './EventTypeHeader';
import FieldConfigTable from './FieldConfigTable';
import AddFieldsModal from './AddFieldsModal';
import AddEventTypeModal from './AddEventTypeModal';
import SaveBar from './SaveBar';
import { AVAILABLE_FIELDS } from './__fixtures__/availableFields';
import { EVENT_TYPES } from './__fixtures__/eventTypes';
import EventFieldConfigModule from '../../../services/apiModules/EventFieldConfigModule';
import { AMLS_COMMON } from '../../constants/globalConstants';
import {
  fromFields,
  renamedFields,
  toAvailableFields,
  toEventTypes,
  toFields
} from './adapters';

/**
 * One instance for every mount of this screen, matching FramlSettings — the
 * sibling tab this page follows. It holds no per-render state, so re-creating it
 * on each render would allocate for nothing.
 *
 * Mounted under AMLS_COMMON, giving /amls/common/eventFieldConfig/... — the path
 * CommonControllerHandler registers.
 */
const api = new EventFieldConfigModule(AMLS_COMMON, {
  'Content-Type': 'application/json'
});

/**
 * FIXTURES ARE THE FALLBACK, NOT THE SOURCE (2026-08-31).
 *
 * The endpoints landed on feat/field-configs-for-eventTimeline, but that branch
 * carries no eventTimeline/scenarios work — so a backend built from it has no
 * /scenarios, and the one we run locally has no /eventFieldConfig. Until they are
 * merged, this screen must work against a deployment where the controller is not
 * mounted at all.
 *
 * The module maps a 404 to null for exactly that case, and null falls back here.
 * REMOVE THIS once the endpoints ship everywhere: a fixture that survives into
 * production is a screen that lies about what is configured.
 */
const fallbackTypes = (category) => EVENT_TYPES[category] || [];

/**
 * Event Fields (Spatial View) — TRM Settings tab, after FRAML Settings.
 *
 * STILL ON FIXTURES. The endpoints do not exist yet, so the two `__fixtures__`
 * modules stand in for `GET /event-types` and `GET /event-fields/available`. Only
 * the loader changes when they ship — every component below already takes its data
 * as props.
 *
 * STATE: the draft lives here, in local state, matching the sibling FRAML Settings
 * tab (which calls NetworkLayer directly rather than going through redux). Nothing
 * else in the app reads this data — the Spatial View re-reads the SAVED config from
 * the server on a different page — so there is no shared state to justify a store,
 * and "unsaved changes" is a diff against what was last fetched, which is natural
 * here and awkward through a store.
 */
const EventFields = () => {
  const [category, setCategory] = useState(EVENT_CATEGORY.ACTIVITY);
  // NOT a hardcoded id. It used to start at 1, which is a fixture id that need
  // not exist on the server — and after a category switch nothing re-selected
  // at all, so the right-hand pane sat empty until you clicked. The effect
  // below selects the first type as soon as the list lands, which is what the
  // design shows (Credit Card, the first, selected on arrival).
  const [selectedId, setSelectedId] = useState(null);
  // Empty until a type is selected and its fields load — NOT seeded from the
  // fixture, whose id space collides with the server's (see loadFields).
  const [fields, setFields] = useState([]);
  // What the server last gave us. `dirty` is a diff against this rather than a
  // flag set by every handler — a flag drifts the moment one handler forgets to
  // set it, and edits that cancel out would still read as unsaved.
  const [baseline, setBaseline] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addTypeOpen, setAddTypeOpen] = useState(false);
  // §7 — the icon picker. Open/closed only; the CHOICE is applied straight away
  // via saveType, so there is no pending-icon state to keep in sync with it.
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  // Set to the type being renamed; the modal is shared between add and edit.
  const [editingType, setEditingType] = useState(undefined);
  // REMOVED 2026-08-31: `draftTypes`, `deletedIds` and `overrides`.
  //
  // All three existed for one reason — there was nowhere to save. A created type
  // had to be held locally, a deletion recorded against a constant fixture list,
  // and a rename layered on top of it. Every one of them vanished on refresh, and
  // together they meant the list on screen was the fixture plus three patches
  // rather than anything the server would agree with.
  //
  // With POST / PUT / DELETE /event-types available, each write calls the server
  // and re-reads the list (see refetchTypes). The server is now the only source,
  // which also fixes the counts and the default flag — both of which the BE
  // computes rather than echoes, so a locally patched list drifted from them.
  //
  // ALSO REMOVED 2026-09-04: the two `id < 0` guards that went with them. A
  // negative id meant "created this session, not yet on the server", which the
  // draft model produced and nothing does now — every id on screen comes from a
  // server read. They were unreachable, and they described a way of working the
  // screen no longer has.

  // ── Server state ───────────────────────────────────────────────────────────
  // Seeded from the fixtures so the first paint is not empty, then replaced by
  // whatever the endpoints return. `null` from the module means 404 — the
  // controller is not mounted — and the fixture stands.
  const [serverTypes, setServerTypes] = useState(() =>
    fallbackTypes(EVENT_CATEGORY.ACTIVITY)
  );
  const [availableFields, setAvailableFields] = useState(
    () => AVAILABLE_FIELDS[EVENT_CATEGORY.ACTIVITY] || []
  );
  const [loadingTypes, setLoadingTypes] = useState(false);
  // Counts SETTLED category loads. Not a loading flag and not derived from the
  // list — see `listKey`, which needs to tell two loads apart even when they
  // return byte-identical ids.
  const [typesLoadId, setTypesLoadId] = useState(0);
  const [loadingFields, setLoadingFields] = useState(false);
  // A message, not a boolean: the only useful thing to show an admin here is
  // WHICH call failed and why, since they cannot retry a specific one.
  const [loadError, setLoadError] = useState(null);

  // Types + the field catalogue for the selected category. Both are
  // category-scoped, so they refetch together.
  useEffect(() => {
    let cancelled = false;
    setLoadingTypes(true);
    setLoadError(null);
    Promise.all([api.getEventTypes(category), api.getAvailableFields(category)])
      .then(([typesRes, fieldsRes]) => {
        if (cancelled) return;
        setServerTypes(
          typesRes ? toEventTypes(typesRes) : fallbackTypes(category)
        );
        setAvailableFields(
          fieldsRes
            ? toAvailableFields(fieldsRes)
            : AVAILABLE_FIELDS[category] || []
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(
          e.message || 'Could not load the event field configuration'
        );
        // Keep the fixture on screen rather than blanking it — a half-empty
        // settings page reads as data loss.
        setServerTypes(fallbackTypes(category));
        setAvailableFields(AVAILABLE_FIELDS[category] || []);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingTypes(false);
        // Mark that A LOAD SETTLED, distinctly from any other load — see
        // `listKey` below. A cancelled load must not bump it: its results were
        // thrown away, so nothing new is on screen to select from.
        setTypesLoadId((n) => n + 1);
      });
    // `cancelled` guards the category being switched again mid-flight: without
    // it the slower response wins and the list disagrees with the tab.
    return () => {
      cancelled = true;
    };
  }, [category]);

  // The selected type's configured fields.
  /**
   * Re-reads the type list after a write. EVERY mutation ends here rather than
   * patching local state: the BE owns id assignment, the field counts and the
   * default flag, so a locally-patched list drifts from the server the moment any
   * of those is computed rather than echoed.
   */
  const refetchTypes = useCallback(
    (cat) =>
      api
        .getEventTypes(cat)
        .then((res) => {
          const next = res ? toEventTypes(res) : fallbackTypes(cat);
          setServerTypes(next);
          return next;
        })
        .catch((e) => {
          message.error(e.message || 'Could not reload the event types');
          return [];
        }),
    []
  );

  /**
   * NO FIXTURE FALLBACK HERE — deliberately, and this is load-bearing.
   *
   * The fixture keys its field lists by ITS OWN ids (1, 2, 3...), which now
   * collide with real server ids. Server type 1 is PASSWORD_CHANGED while fixture
   * 1 is Login, so falling back showed LOGIN'S FIELDS UNDER PASSWORD CHANGED —
   * five rows that looked saved (baseline was set from them, so Save was
   * correctly disabled) and vanished on the next successful load. Reported as
   * "my fields disappeared"; nothing had ever been saved (2026-08-31).
   *
   * An empty table is honest. Another type's configuration presented as this
   * type's is not, and on a settings screen it invites someone to "fix" a
   * configuration that was never there.
   *
   * The TYPE LIST still falls back, because a 404 there means the whole feature
   * is undeployed and the fixture is then self-consistent — ids and all.
   */
  // `availableFieldsRef` rather than the state itself: loadFields is memoised
  // with an empty dep list (it is called from effects and handlers alike), so
  // closing over the state value would pin whatever the catalogue was on first
  // render — empty, at that point.
  const availableFieldsRef = useRef([]);
  useEffect(() => {
    availableFieldsRef.current = availableFields;
  }, [availableFields]);

  const loadFields = useCallback((id) => {
    if (id == null) return Promise.resolve([]);
    setLoadingFields(true);
    setLoadError(null);
    return api
      .getEventTypeFields(id)
      .then((res) => (res ? toFields(res, availableFieldsRef.current) : []))
      .catch((e) => {
        setLoadError(
          e.message || 'Could not load the fields for this event type'
        );
        return [];
      })
      .finally(() => setLoadingFields(false));
  }, []);

  /**
   * The event type list, with the SELECTED row's counts kept live.
   *
   * LIVE COUNTS VIA A MEASURED OFFSET. The two endpoints count differently and
   * the difference cannot be read from the payload: `/event-types` counts
   * STORED `fp_event_type_field` rows, while `/event-types/{id}/fields`
   * SYNTHESISES the identifier row when it is not stored — and a synthesised
   * row is indistinguishable from a stored one.
   *
   * So rather than guess the rule, measure it. `baseline` is the field list
   * exactly as the server returned it, so
   *     offset = the server's count − baseline's count
   * captures whatever that server leaves out, per type, without naming it.
   * Applying the offset to the live draft gives a number that equals the
   * server's on load (so selecting a row never changes it) and still moves as
   * you tick (so it is not stale).
   *
   * Two earlier attempts failed here, both on 2026-09-02: counting the live
   * list raw made ACCOUNT_PASSWORD_RESET jump by one, and subtracting the
   * identifier moved that drift onto the three types whose identifier IS
   * stored — one wrong row became three.
   *
   * Until the fields load, the server's own number stands, so a row is never
   * shown a count derived from an empty list.
   */
  const types = useMemo(() => {
    const tally = (list, key) => list.filter((f) => f[key]).length;
    // Server state, not the fixture — `serverTypes` already falls back to the
    // fixture when the endpoint is absent.
    return serverTypes.map((t) => {
      if (t.id !== selectedId || !baseline.length) return t;
      return {
        ...t,
        spatialFieldCount:
          tally(fields, 'showSpatial') +
          (t.spatialFieldCount - tally(baseline, 'showSpatial')),
        onClickFieldCount:
          tally(fields, 'showOnClick') +
          (t.onClickFieldCount - tally(baseline, 'showOnClick'))
      };
    });
  }, [serverTypes, selectedId, fields, baseline]);

  const selected = useMemo(
    () => types.find((t) => t.id === selectedId),
    [types, selectedId]
  );

  /**
   * SELECT THE FIRST TYPE once the list is known.
   *
   * The design lands with the first entry selected and its fields shown — an
   * empty right-hand pane reads as "this type has no fields" rather than "pick
   * one". Runs whenever the list changes, so it covers first load AND the
   * category switch, which deliberately clears the selection because the old
   * id belongs to the other category's list.
   *
   * Keyed on the ids rather than the array identity: every refetch after a save
   * produces a new array, and re-running on that would fight a selection the
   * user has since made. Re-selects only when the current one has gone —
   * deleted, or from the category we just left.
   *
   * `loadType` rather than `setSelectedId`, so the fields load with it.
   */
  const typeIds = types.map((t) => t.id).join(',');

  /* `typeIds` ALONE IS NOT ENOUGH TO IDENTIFY A LOAD (raised in review — this is
     the second half of FIN-13579).

     Switching away and straight back — Activity → Transaction → Activity, faster
     than the first fetch resolves — cancels the Transaction load, so the ids
     never change: the key reads "24,25" before and after. The effect therefore
     never re-runs, and the selection cleared by `switchCategory` is never
     restored: a populated list beside an empty panel, which is the very symptom
     QA raised. The guard on re-clicking the active category does not cover this,
     because each of these clicks IS a real category change.

     Pairing the ids with a counter of settled loads distinguishes the two.
     `typeIds` still belongs in the key: a delete goes through `refetchTypes`,
     which changes the ids without going near the category fetch. */
  const listKey = `${typesLoadId}:${typeIds}`;
  useEffect(() => {
    if (!types.length) return;
    if (selectedId != null && types.some((t) => t.id === selectedId)) return;
    loadType(types[0].id);
    // Intentionally keyed on `listKey` alone. `types`, `selectedId` and
    // `loadType` are all read here but must NOT re-trigger it: `types` is a new
    // array on every render, and re-running on a selection change would undo
    // the click that caused it. (The exhaustive-deps rule is not enabled in
    // this project, so this note is the only thing recording the choice.)
  }, [listKey]);

  const dirty = useMemo(
    () => JSON.stringify(fields) !== JSON.stringify(baseline),
    [fields, baseline]
  );

  /**
   * PER-TYPE DRAFTS (2026-08-31). Unsaved edits are kept when you switch away and
   * restored when you come back, so navigating the list NEVER discards work.
   *
   * This replaced a "Discard unsaved changes?" confirm. The confirm was correct
   * about the risk but solved it by asking rather than by preserving; removing it
   * alone would have discarded silently, which is worse. Holding the edits fixes
   * both — nothing is lost, so there is nothing to warn about.
   *
   * A ref, not state: nothing renders from it directly. Keeping it in state would
   * re-render the page on every switch for no visual change.
   *
   * `baseline` is stored ALONGSIDE the fields, because `dirty` is a diff between
   * them — restoring fields without their baseline would make a restored draft
   * look clean, and Save would sit disabled over real changes.
   */
  const drafts = useRef({});

  const loadType = (id) => {
    // Park whatever is on screen before moving. Only when it differs from its
    // baseline: parking a clean copy would pin a stale snapshot and hide later
    // server-side changes on the way back.
    setSelectedId((prevId) => {
      if (
        prevId != null &&
        JSON.stringify(fields) !== JSON.stringify(baseline)
      ) {
        drafts.current[prevId] = { fields, baseline };
      }
      return id;
    });

    if (id == null) {
      setFields([]);
      setBaseline([]);
      return;
    }

    // Restore an unsaved draft rather than refetching over it.
    const parked = drafts.current[id];
    if (parked) {
      setFields(parked.fields);
      setBaseline(parked.baseline);
      return;
    }

    loadFields(id).then((loaded) => {
      setFields(loaded);
      setBaseline(loaded);
    });
  };

  // NO CONFIRM ON SWITCHING TYPES — and none is needed, because loadType parks
  // the edits rather than discarding them (see the drafts ref above). Story §1
  // asks that navigating away follow the standard unsaved-change behaviour; this
  // satisfies the intent by not losing anything in the first place.
  const selectType = (id) => loadType(id);

  const switchCategory = (next) => {
    // FIN-13579 — RE-CLICKING THE ACTIVE CATEGORY MUST NOT DESELECT.
    //
    // Clearing the selection below relies on the effect above to restore it,
    // and that effect only re-runs when the list of ids changes or a load
    // settles. Choosing the category you are ALREADY on does neither — the
    // guard below returns before the fetch — so the selection would stay
    // cleared: a populated list beside an empty "Select an event type, or add
    // one" panel.
    //
    // A double-click guarantees it — the first click switches and is restored,
    // the second re-selects the same category and is not. That is what QA
    // reported as "double-clicking does nothing".
    //
    // Guarding here rather than making the effect depend on `category`: the
    // effect must NOT re-run on a selection change, or it would undo the click
    // that caused it (see its own note above).
    if (next === category) return;

    setCategory(next);
    // The list for `next` has not arrived yet — the effect above fetches it — so
    // selection is cleared and restored when it lands.
    loadType(undefined);
  };

  const toggleField = (fieldKey, column) =>
    setFields((prev) =>
      prev.map((f) =>
        f.fieldKey === fieldKey ? { ...f, [column]: !f[column] } : f
      )
    );

  // A null label means "use the schema's own name" — the BE stores display_label
  // as nullable for exactly that. The schema name is then resolved at render time
  // from the available-fields list, so an admin who clears a rename gets the
  // current schema wording rather than a stale copy of it.
  // PARKED, like the pencil that would call it — see FieldConfigTable's
  // RENAME_ENABLED. The logic lives in adapters.renamedFields so it stays tested
  // while the entry point is off.
  //
  // NOTE, unchanged in the extraction: this resolves against AVAILABLE_FIELDS,
  // the FIXTURE, not the live `availableFields` state. Almost certainly a
  // leftover — clearing a rename should fall back to the schema the server is
  // actually serving. Left as-is rather than fixed silently, since the whole
  // path is parked; worth correcting whenever rename is un-parked.
  const renameField = (fieldKey, label) =>
    setFields((prev) =>
      renamedFields(prev, fieldKey, label, AVAILABLE_FIELDS[category])
    );

  const deleteField = (fieldKey) =>
    setFields((prev) => prev.filter((f) => f.fieldKey !== fieldKey));

  // §9: the technical name comes from the schema and the display name is seeded
  // from it — the admin may then edit the label, never the key.
  const addFields = (schemaEntries) => {
    setFields((prev) => [
      ...prev,
      ...schemaEntries.map((entry, i) => ({
        /* FIN-13616 — `fieldKey`, NOT `technicalName`. The config stores and the
           BE validates against `custom_field.name`; `technical_name` is the
           StarRocks column it maps to, and the two are different strings for
           several transaction fields:
               fieldKey         technicalName
               receiver_amount  receiver_transaction_currency_amount
               txn_type         transaction_type_description
               receiver_type    receiver_party_type
           Adding "Amount Received by the Receiver" therefore sent
           `receiver_transaction_currency_amount` and the save failed WHOLE with
           `Unknown field keys` — not just that field, the entire Event Fields
           configuration for the type could not be saved.
           It went unnoticed because the two columns AGREE for every activity
           field (`ip_address` -> `ip_address`), and AddFieldsModal had already
           been corrected for exactly this in its own matching logic.
           `?? technicalName` keeps the FIXTURE path working: __fixtures__/
           availableFields.js predates the endpoint and carries only
           `technicalName`, and index.js uses it RAW on a 404. */
        fieldKey: entry.fieldKey ?? entry.technicalName,
        displayLabel: entry.displayName,
        showSpatial: false,
        showOnClick: false,
        sortOrder: prev.length + i
      }))
    ]);
    setPickerOpen(false);
  };

  /**
   * POST /event-types, then refetch and select what the server created.
   *
   * The identifier field is NOT sent. §12/§13 make it always present and
   * undeletable, and the BE seeds it on create — sending it too would either
   * duplicate it or fight whatever the server chose.
   */
  const addType = ({ displayName, technicalValue, iconKey }) => {
    setSaving(true);
    return api
      .createEventType({
        displayName,
        technicalValue,
        iconKey: iconKey || null,
        eventCategory: category,
        fields: []
      })
      .then((created) => {
        if (created === null) {
          message.info(
            'Creating event types is not available on this environment yet.'
          );
          return undefined;
        }
        setAddTypeOpen(false);
        return refetchTypes(category).then((list) => {
          // Match on technicalValue, not on the response body: the create
          // response shape is the BE's to change, while the unique key
          // (source_id, event_category, technical_value) is a contract.
          const mine =
            list.find((t) => t.technicalValue === technicalValue) || created;
          if (mine?.id != null) loadType(mine.id);
          message.success(`"${displayName}" created`);
          return undefined;
        });
      })
      .catch((e) => {
        message.error(e.message || 'Could not create the event type');
        return undefined;
      })
      .finally(() => setSaving(false));
  };

  /**
   * §7: "Changing the icon affects future rendering of Spatial View without
   * requiring frontend deployment" and "changes all existing spatial views" —
   * both follow from storing it against the type, which is what saveType does.
   *
   * Applied IMMEDIATELY rather than held as an unsaved change: the icon lives on
   * the event TYPE (a PUT /event-types/:id), while Save changes covers the FIELD
   * list (a PUT .../fields). Mixing them would make one button responsible for
   * two endpoints and leave "unsaved changes" ambiguous about which it meant.
   */
  const pickIcon = (iconKey) => {
    setIconPickerOpen(false);
    if (!selected) return undefined;
    return saveType({
      id: selected.id,
      displayName: selected.displayName,
      technicalValue: selected.technicalValue,
      iconKey
    });
  };

  /**
   * PUT /event-types/:id — rename, correct the technical value, or change the icon.
   *
   * `technicalValue` IS sent (FIN-13930). 7d13a65ce2 had dropped it from
   * EventTypeUpdateRequest, so a corrected value saved "successfully" and never
   * changed. The BE now treats it as optional: an unchanged value is a no-op, a
   * changed one is re-checked for uniqueness (409) and rejected on the system
   * default type.
   */
  const saveType = ({ id, displayName, technicalValue, iconKey }) => {
    if (id === undefined) {
      // technicalValue COMES FROM THE MODAL and must be forwarded. It was
      // hardcoded to '' here while save was a stub and nothing left the browser;
      // once the POST became real that empty value was rejected by the BE and the
      // type silently never appeared (2026-08-31). It is also the unique key —
      // (source_id, event_category, technical_value) — so an empty one is not a
      // harmless default, it is an invalid row.
      return addType({ displayName, technicalValue, iconKey });
    }
    const existing = types.find((t) => t.id === id);
    setSaving(true);
    return api
      .updateEventType(id, {
        displayName,
        technicalValue,
        iconKey: iconKey ?? existing?.iconKey ?? null,
        isDefault: Boolean(existing?.isDefault)
      })
      .then((res) => {
        if (res === null) {
          message.info('Editing is not available on this environment yet.');
          return undefined;
        }
        setEditingType(undefined);
        return refetchTypes(category).then(() => undefined);
      })
      .catch((e) => {
        message.error(e.message || 'Could not update the event type');
        return undefined;
      })
      .finally(() => setSaving(false));
  };

  /**
   * DELETE /event-types/:id, then move selection off it.
   *
   * Selection moves only AFTER the refetch confirms the deletion — jumping first
   * and failing would leave the admin on a different type with no explanation of
   * why the one they deleted is still there.
   */
  const deleteType = (id) => {
    setSaving(true);
    return api
      .deleteEventType(id)
      .then((res) => {
        if (res === null) {
          message.info('Deleting is not available on this environment yet.');
          return undefined;
        }
        // The type is gone, so its parked draft can never be saved — dropping it
        // stops a deleted type's edits reappearing if the same id is reissued.
        delete drafts.current[id];
        return refetchTypes(category).then((list) => {
          if (id === selectedId) {
            const next = list.find((t) => t.id !== id);
            loadType(next ? next.id : undefined);
          }
          message.success('Event type deleted');
          return undefined;
        });
      })
      .catch((e) => {
        message.error(e.message || 'Could not delete the event type');
        return undefined;
      })
      .finally(() => setSaving(false));
  };

  /**
   * ONE DEFAULT PER CATEGORY. Setting a new one has to clear the old, or two
   * types both claim the fallback and which wins becomes whichever the BE
   * happens to return first.
   *
   * Enforced HERE rather than server-side because only the page can see the whole
   * list — so turning one on issues a PUT for the previous holder as well. If the
   * BE later enforces it, this collapses to a single call and the second PUT
   * becomes a no-op rather than a conflict.
   */
  const toggleDefault = (id) => {
    const target = types.find((t) => t.id === id);
    if (!target) return undefined;
    const turningOn = !target.isDefault;
    const previous = types.find((t) => t.isDefault && t.id !== id);

    const put = (t, isDefault) =>
      api.updateEventType(t.id, {
        displayName: t.displayName,
        technicalValue: t.technicalValue,
        iconKey: t.iconKey ?? null,
        isDefault
      });

    setSaving(true);
    return Promise.all(
      [
        put(target, turningOn),
        // Only when turning ON — turning off leaves the category with no default,
        // which is a legitimate state and needs no second call.
        turningOn && previous ? put(previous, false) : null
      ].filter(Boolean)
    )
      .then(([first]) => {
        if (first === null) {
          message.info(
            'Changing the default is not available on this environment yet.'
          );
          return undefined;
        }
        return refetchTypes(category).then(() => undefined);
      })
      .catch((e) => {
        message.error(e.message || 'Could not change the default event type');
        return undefined;
      })
      .finally(() => setSaving(false));
  };

  /**
   * PUT /event-types/:id/fields — the whole list, not a delta. `SaveFieldsRequest`
   * REPLACES, so sending a subset would silently delete the rest; `fromFields`
   * sends everything and re-derives sortOrder from position.
   *
   * REFETCHES rather than trusting the local array as the new truth: the BE
   * derives isIdentifier and isDeprecated on read, so the saved state can
   * legitimately differ from what was sent. Re-reading is also what turns the
   * screen honest after a partial failure.
   */
  const save = () => {
    if (selectedId == null) return;
    setSaving(true);
    api
      .saveEventTypeFields(selectedId, fromFields(fields))
      .then((res) => {
        if (res === null) {
          // 404 — the controller is not mounted on this deployment.
          message.info(
            'Save is not available on this environment yet — the endpoint is not deployed.'
          );
          return;
        }
        message.success('Field configuration saved');
        // The parked draft is now stale — this type is saved, so the next visit
        // should read the server's copy, not a snapshot of what we just sent.
        delete drafts.current[selectedId];
        return loadFields(selectedId).then((saved) => {
          setFields(saved);
          setBaseline(saved);
        });
      })
      .catch((e) =>
        // The edits stay on screen. Clearing them on failure would lose work the
        // admin can still retry, and the Save bar stays enabled because `dirty`
        // is a diff against `baseline`, which has not moved.
        message.error(e.message || 'Could not save the field configuration')
      )
      .finally(() => setSaving(false));
  };

  const noop = () => {};

  return (
    <div className={style.page}>
      <div className={style.pageHead}>
        <div>
          <h2 className={style.pageTitle}>Event Fields (Spatial View)</h2>
          <p className={style.pageDescription}>
            Define which fields are shown for each kind of event in the spatial
            alert view — both on the timeline node and in the detail panel
            opened on click. Configuration is keyed by{' '}
            <code>activity_type</code> and <code>txn_type</code>. You can add
            new activity or transaction types at any time, including ones not
            present in your data yet.
          </p>
        </div>
        <SaveBar dirty={dirty} saving={saving} onSave={save} />
      </div>

      <CategoryTabs value={category} onChange={switchCategory} />

      <div className={style.pageBody}>
        <aside className={style.pageAside}>
          <EventTypeList
            types={types}
            selectedId={selectedId}
            category={category}
            onSelect={selectType}
            onAddNew={() => setAddTypeOpen(true)}
          />
        </aside>

        <section className={style.pageMain}>
          {selected ? (
            <>
              <EventTypeHeader
                type={selected}
                category={category}
                onEdit={() => setEditingType(selected)}
                onDelete={deleteType}
                onToggleDefault={toggleDefault}
                onPickIcon={() => setIconPickerOpen(true)}
                iconPickerOpen={iconPickerOpen}
                onPickIconCancel={() => setIconPickerOpen(false)}
                onIconPicked={pickIcon}
              />

              {/* The design bands the helper text and its button together —
                  `px-4 py-3 border-b bg-muted/30 space-y-2` — and butts the
                  table straight against that border, rather than floating both
                  inside one padded box as we did. */}
              <div className={style.schemaBand}>
                {/* DIFFERENT COPY for the system-default type. The standard
                    note is about ADDING fields from the schema — advice this
                    type cannot act on, since its key set is frozen. The design
                    replaces it with what the type IS and what can still be
                    changed, which is the question its greyed controls raise. */}
                {isSystemType(selected) ? (
                  <p className={style.schemaNote}>
                    Seeded by the product and used whenever an incoming{' '}
                    {category === EVENT_CATEGORY.ACTIVITY
                      ? 'activity'
                      : 'transaction'}{' '}
                    has no configured{' '}
                    {category === EVENT_CATEGORY.ACTIVITY
                      ? 'activity_type'
                      : 'txn_type'}{' '}
                    (and no client default is set). This type and its seed
                    fields cannot be removed — you can only choose which of them
                    are displayed.
                  </p>
                ) : (
                  <p className={style.schemaNote}>
                    Fields come from the API schema for{' '}
                    {category === EVENT_CATEGORY.ACTIVITY
                      ? 'activity'
                      : 'transaction'}{' '}
                    events. Pick the ones you expect on this type; they render
                    as soon as matching events are ingested. To introduce a
                    field that is not in the schema, add it under Custom Fields
                    first.
                  </p>
                )}

                {/* The popover ANCHORS to this button, so it wraps the
                    trigger rather than being rendered loose at the end of the
                    page as a modal was. */}
                {/* AC-2: the system-default type's field KEYS are fixed —
                    `PUT .../fields` rejects any change to the set. The whole
                    picker goes, trigger included, rather than opening onto a
                    choice the server will refuse. The two checkboxes in the
                    table below remain editable, which is the one change that
                    is still allowed. */}
                {isSystemType(selected) ? null : (
                  <AddFieldsModal
                    open={pickerOpen}
                    category={category}
                    availableFields={availableFields}
                    configuredKeys={fields.map((f) => f.fieldKey)}
                    onAdd={addFields}
                    onCancel={() => setPickerOpen(false)}
                  >
                    <Button
                      type="primary"
                      className={style.addFieldsButton}
                      icon={<PlusOutlined />}
                      onClick={() => setPickerOpen(true)}
                    >
                      Add fields from schema
                    </Button>
                  </AddFieldsModal>
                )}
              </div>

              <div className={style.fieldTableSlot}>
                <FieldConfigTable
                  fields={fields}
                  category={category}
                  onToggle={toggleField}
                  onDelete={deleteField}
                  onRename={renameField}
                  maxSpatial={MAX_SPATIAL_FIELDS}
                  maxOnClick={MAX_ON_CLICK_FIELDS}
                  systemType={isSystemType(selected)}
                />
              </div>
            </>
          ) : (
            <Empty
              className={style.emptyType}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Select an event type, or add one"
            />
          )}
        </section>
      </div>

      <AddEventTypeModal
        open={addTypeOpen || Boolean(editingType)}
        category={category}
        editing={editingType}
        existingValues={types.map((t) => t.technicalValue)}
        onAdd={saveType}
        onCancel={() => {
          setAddTypeOpen(false);
          setEditingType(undefined);
        }}
      />
    </div>
  );
};

export default EventFields;
